/**
 * Terrain generation utilities
 * Migrated from test.html prototype
 */

import * as THREE from 'three';
import { hash2, fbm } from '@/core/utils/noise';
import type { FactionId, GameConfig, TerrainType, UnitType } from '@/types';
import { DEFAULT_CONFIG, FACTION_TERRAIN } from '@/types';
import {
  getTerrainAttackModifier,
  getTerrainDefenseModifier,
  getTerrainSpeedModifier as getTerrainSpeedMod,
  getTerrainModifier,
} from '@/game/systems/TerrainCombatSystem';

// Faction colors for all 7 factions (indexed 0-6)
// Enhanced saturation to match reference design
export const FACTION_COLORS: Record<number, THREE.Color> = {
  0: new THREE.Color('#D64541'), // 炎国 Yan - 朱红 (more saturated)
  1: new THREE.Color('#4A90D9'), // 寒国 Han - 冰蓝 (deeper blue)
  2: new THREE.Color('#5DAE5D'), // 林国 Lin - 翠绿 (richer green)
  3: new THREE.Color('#E8C547'), // 商国 Shang - 鎏金 (warm gold)
  4: new THREE.Color('#6A1B9A'), // 玄国 Xuan - 暗紫 (deeper purple)
  5: new THREE.Color('#78909C'), // 铁国 Tie - 钢灰 (steel gray)
  6: new THREE.Color('#29B6F6'), // 云国 Yun - 天青 (sky blue)
};

// Faction ID mapping from index to FactionId
export const FACTION_INDEX_TO_ID: Record<number, FactionId> = {
  0: 'yan',
  1: 'han',
  2: 'lin',
  3: 'shang',
  4: 'xuan',
  5: 'tie',
  6: 'yun',
};

// Extended faction colors for all 7 factions by ID
// Enhanced saturation to match reference design
export const EXTENDED_FACTION_COLORS: Record<FactionId, THREE.Color> = {
  yan: new THREE.Color('#D64541'),   // Red - Yan (more saturated)
  han: new THREE.Color('#4A90D9'),   // Blue - Han (deeper blue)
  lin: new THREE.Color('#5DAE5D'),   // Green - Lin (richer green)
  shang: new THREE.Color('#E8C547'), // Gold - Shang (warm gold)
  xuan: new THREE.Color('#6A1B9A'),  // Purple - Xuan (deeper purple)
  tie: new THREE.Color('#78909C'),   // Steel - Tie (steel gray)
  yun: new THREE.Color('#29B6F6'),   // Sky Blue - Yun (sky blue)
  neutral: new THREE.Color('#888888'), // Gray - Neutral
};

/**
 * Gets the faction ID for a territory index
 */
export function getFactionIdFromIndex(index: number): FactionId {
  return FACTION_INDEX_TO_ID[index] || 'neutral';
}

/**
 * 7个国家的核心区域位置
 * 按地理逻辑分布：北寒南热，东西各有特色
 *
 * 布局：
 *        寒(北)
 *    铁       云
 *  玄    中心    林
 *    商       炎
 *        (南)
 */
const FACTION_SEEDS: Array<{ x: number; z: number; faction: number }> = [
  // 0: 炎国 (yan) - 东南，火山地带
  { x: 55, z: 65, faction: 0 },
  // 1: 寒国 (han) - 正北，雪原
  { x: 0, z: -80, faction: 1 },
  // 2: 林国 (lin) - 正东，森林
  { x: 85, z: 0, faction: 2 },
  // 3: 商国 (shang) - 西南，草原
  { x: -55, z: 60, faction: 3 },
  // 4: 玄国 (xuan) - 正西，沼泽
  { x: -85, z: -10, faction: 4 },
  // 5: 铁国 (tie) - 西北，山丘
  { x: -50, z: -65, faction: 5 },
  // 6: 云国 (yun) - 东北，荒漠
  { x: 50, z: -60, faction: 6 },
];

/**
 * 计算点到国家核心的距离，使用噪声扰动创建自然边界
 */
function distortedDistance(wx: number, wz: number, seedX: number, seedZ: number): number {
  const dx = wx - seedX;
  const dz = wz - seedZ;
  const baseDist = Math.sqrt(dx * dx + dz * dz);

  // 使用噪声扰动距离，创建不规则边界
  const distortScale = 0.03;
  const distortion = fbm(wx * distortScale + 1000, wz * distortScale + 1000) * 25;
  const distortion2 = fbm(wx * 0.05 + 500, wz * 0.05 + 500) * 15;

  return baseDist + distortion + distortion2;
}

/**
 * Determines which faction controls a tile based on Voronoi-like territory regions
 * 使用7个固定的核心区域，通过噪声扰动产生自然的边界
 */
export function territoryIndex(wx: number, wz: number, _config: GameConfig = DEFAULT_CONFIG): number {
  let bestDist = Infinity;
  let bestFaction = 0;

  // 找到最近的国家核心
  for (const seed of FACTION_SEEDS) {
    const dist = distortedDistance(wx, wz, seed.x, seed.z);
    if (dist < bestDist) {
      bestDist = dist;
      bestFaction = seed.faction;
    }
  }

  return bestFaction;
}

/**
 * 获取到最近国家核心的距离（用于计算地形强度）
 */
function getDistanceToFactionCore(wx: number, wz: number): { faction: number; distance: number; secondDistance: number } {
  let distances: Array<{ faction: number; dist: number }> = [];

  for (const seed of FACTION_SEEDS) {
    const dist = distortedDistance(wx, wz, seed.x, seed.z);
    distances.push({ faction: seed.faction, dist });
  }

  distances.sort((a, b) => a.dist - b.dist);

  return {
    faction: distances[0].faction,
    distance: distances[0].dist,
    secondDistance: distances[1]?.dist || Infinity,
  };
}

/**
 * Height steps for quantized terrain
 */
const HEIGHT_STEPS = [0.35, 0.8, 1.2, 1.6, 2.1, 2.6, 3.1, 3.6];

/**
 * Map configuration for island-like terrain
 */
const MAP_RADIUS = 120; // Radius of the main landmass (increased for more land)
const OCEAN_FALLOFF = 40; // How quickly land transitions to ocean at edges

/**
 * Computes tile height using FBM noise with quantization steps
 * Creates an island-like landmass with ocean around the edges
 */
export function tileHeight(wx: number, wz: number): number {
  // Base noise height
  const noiseH = fbm(wx * 0.08, wz * 0.08) * 2.0 + fbm(wx * 0.02 + 100, wz * 0.02 - 60) * 2.0;

  // Distance from center (0, 0)
  const dist = Math.sqrt(wx * wx + wz * wz);

  // Island falloff: 1.0 at center, falls off to negative at edges
  // This creates land in the center and ocean at the edges
  let islandFactor: number;
  if (dist < MAP_RADIUS) {
    // Inside main landmass - full height
    islandFactor = 1.0;
  } else if (dist < MAP_RADIUS + OCEAN_FALLOFF) {
    // Transition zone - gradual falloff with some noise for coastline variation
    const t = (dist - MAP_RADIUS) / OCEAN_FALLOFF;
    const coastNoise = fbm(wx * 0.1 + 200, wz * 0.1 + 200) * 0.3;
    islandFactor = 1.0 - t * (1.5 - coastNoise);
  } else {
    // Ocean - always below water level
    islandFactor = -0.5;
  }

  // Apply island factor to height
  const h = noiseH * Math.max(0, islandFactor) + islandFactor * 0.5;

  // Quantize to height steps
  const stepIndex = Math.max(0, Math.min(HEIGHT_STEPS.length - 1, Math.floor(h)));
  let q = HEIGHT_STEPS[stepIndex];

  // If in ocean zone, force water
  if (islandFactor < 0) {
    q = 0.2; // Below water level
  }

  // Random tall tiles (only on land)
  if (q >= 0.5 && hash2(Math.floor(wx) + 999, Math.floor(wz) + 777) > 0.988) {
    q += 0.8;
  }

  return q;
}

/**
 * Beach/shore detection - tiles near water level
 */
export function isBeach(wx: number, wz: number, h: number): boolean {
  if (h < 0.5 || h > 0.9) return false;

  // Check if any neighboring tile is water
  const checkDist = 2.0;
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      if (dx === 0 && dz === 0) continue;
      const nh = tileHeight(wx + dx * checkDist, wz + dz * checkDist);
      if (!isLand(nh)) return true;
    }
  }
  return false;
}

/**
 * Computes tile color - starts with neutral color
 * Territory colors are applied dynamically by TerritorySystem
 */
export function tileColor(wx: number, wz: number, h: number): THREE.Color {
  // Use neutral color for initial terrain - territory system will update dynamically
  return getTerritoryColor(wx, wz, h, null);
}

/**
 * Determines if a tile is land (above water level)
 */
export function isLand(h: number): boolean {
  return h >= 0.5;
}

/**
 * Height multiplier used for terrain visualization
 * Must match the value in Chunk.ts
 */
export const TERRAIN_HEIGHT_MULTIPLIER = 3.0;

/**
 * Gets the Y position for a tile mesh
 */
export function getTileYPosition(h: number, isLandTile: boolean): number {
  if (!isLandTile) {
    return -0.6;
  }
  return h * 0.5;
}

/**
 * Gets the Y position for objects on the terrain surface (units, buildings)
 * This accounts for the height multiplier used in terrain rendering
 */
export function getSurfaceY(wx: number, wz: number): number {
  const h = tileHeight(wx, wz);
  if (!isLand(h)) return 0;

  // Check if this is a beach tile
  const isBeachTile = isBeach(wx, wz, h);

  if (isBeachTile) {
    // Beach tiles have lower profile
    return h * 0.8;
  }

  // Regular land tiles use the height multiplier
  return h * TERRAIN_HEIGHT_MULTIPLIER;
}

/**
 * 获取国家派系ID
 */
function getFactionIdFromTerritoryIndex(index: number): FactionId {
  const mapping: Record<number, FactionId> = {
    0: 'yan',
    1: 'han',
    2: 'lin',
    3: 'shang',
    4: 'xuan',
    5: 'tie',
    6: 'yun',
  };
  return mapping[index] || 'neutral';
}

/**
 * Determines terrain type based on faction territory and noise
 * 每个国家有其特色地形，从核心向边界过渡到中性地形
 */
export function getTerrainType(wx: number, wz: number, h: number): TerrainType {
  // Water check
  if (!isLand(h)) {
    return 'water';
  }

  // 获取当前位置的国家信息
  const { faction, distance, secondDistance } = getDistanceToFactionCore(wx, wz);
  const factionId = getFactionIdFromTerritoryIndex(faction);
  const factionTerrain = FACTION_TERRAIN[factionId];

  // 计算地形强度：距离核心越近，特色地形越强
  // 增大区域让地形更连续
  const coreRadius = 55;  // 核心区域半径（增大）
  const transitionRadius = 95; // 过渡区域半径（增大）

  // 边界模糊度：降低影响，让地形延伸到边界
  const boundaryFactor = Math.min(1, (secondDistance - distance) / 20 + 0.3);

  // 使用多层噪声创建有机的地形分布（降低噪声频率让区域更大）
  const n1 = fbm(wx * 0.015 + 300, wz * 0.015 + 300);
  const n2 = fbm(wx * 0.035 + 100, wz * 0.035 + 200);
  const detailNoise = n1 * 0.7 + n2 * 0.3;

  // 特色地形强度：核心区域高，边界也保持较高
  let terrainStrength: number;
  if (distance < coreRadius) {
    // 核心区域：高强度特色地形
    terrainStrength = 0.95 + detailNoise * 0.05;
  } else if (distance < transitionRadius) {
    // 过渡区域：缓慢减弱，保持较高强度
    const t = (distance - coreRadius) / (transitionRadius - coreRadius);
    terrainStrength = (1 - t * 0.5) * 0.85 + detailNoise * 0.2;
  } else {
    // 边缘区域：仍有一定强度
    terrainStrength = 0.4 + detailNoise * 0.25;
  }

  // 边界区域适度降低特色地形强度
  terrainStrength *= boundaryFactor;

  // 高度也影响地形选择
  // 高海拔更可能是雪/山丘
  if (h >= 2.0) {
    // 高海拔：根据最近的国家决定是雪还是山丘
    if (factionId === 'han' || detailNoise < -0.2) {
      return 'snow';
    }
    if (factionId === 'tie' || detailNoise > 0.2) {
      return 'highland';
    }
    return detailNoise < 0 ? 'snow' : 'highland';
  }

  // 计算距离地图中心的距离
  const distFromCenter = Math.sqrt(wx * wx + wz * wz);
  const centerRadius = 20; // 中心平原区域半径（缩小）

  // 地图中心区域强制为平原（只有很小的中心区域）
  if (distFromCenter < centerRadius) {
    return 'plain';
  }

  // 根据地形强度决定是特色地形还是中性地形
  // 降低阈值让更多区域显示特色地形
  const threshold = 0.3 + detailNoise * 0.15;

  if (terrainStrength > threshold) {
    // 使用国家特色地形
    return factionTerrain;
  }

  // 边界/过渡区域：使用中性地形
  if (h >= 1.4) {
    return 'plain';
  }

  // 低海拔边界区域：平原
  return 'plain';
}

/**
 * Gets movement speed modifier for terrain at given position
 * 使用新的地形战斗系统，支持兵种特定修正
 */
export function getTerrainSpeedModifier(
  wx: number,
  wz: number,
  factionId?: FactionId,
  unitType?: UnitType
): number {
  const h = tileHeight(wx, wz);
  const terrainType = getTerrainType(wx, wz, h);

  // 如果有兵种类型，使用新的地形战斗系统
  if (unitType) {
    return getTerrainSpeedMod(terrainType, unitType, factionId);
  }

  // 向后兼容：没有兵种类型时使用默认值
  return 1.0;
}

/**
 * Computes dynamic tile color based on faction ownership
 * Used when territory changes hands during gameplay
 */
export function getTerritoryColor(wx: number, wz: number, h: number, factionId: FactionId | null): THREE.Color {
  // Beach/shore tiles always get sandy color
  if (isBeach(wx, wz, h)) {
    const beachColor = new THREE.Color('#d4c4a8');
    const jitter = (hash2(Math.floor(wx) + 5, Math.floor(wz) + 5) - 0.5) * 0.05;
    beachColor.offsetHSL(0.01 * jitter, 0, jitter * 0.5);
    return beachColor;
  }

  // Get base faction color (use neutral gray if no owner)
  const baseColor = factionId
    ? EXTENDED_FACTION_COLORS[factionId].clone()
    : new THREE.Color('#6a6a6a'); // Neutral/unclaimed territory

  const terrainType = getTerrainType(wx, wz, h);

  // 地形颜色混合 - 增强地面纹理区分度
  // 混合比例较高，让地形特征明显
  switch (terrainType) {
    case 'forest':
      // 深绿色森林地面
      baseColor.lerp(new THREE.Color('#2d5a27'), 0.35);
      break;
    case 'highland':
      // 灰色岩石地面
      baseColor.lerp(new THREE.Color('#6a6a6a'), 0.4);
      baseColor.lerp(new THREE.Color('#5a5a5a'), 0.1);
      break;
    case 'snow':
      // 白色雪地
      baseColor.lerp(new THREE.Color('#e8f4f8'), 0.55);
      baseColor.lerp(new THREE.Color('#ffffff'), 0.15);
      break;
    case 'desert':
      // 沙黄色荒漠
      baseColor.lerp(new THREE.Color('#d4b896'), 0.45);
      baseColor.lerp(new THREE.Color('#c9a86c'), 0.1);
      break;
    case 'volcano':
      // 深红/棕色火山地面
      baseColor.lerp(new THREE.Color('#4a2020'), 0.35);
      baseColor.lerp(new THREE.Color('#6b3030'), 0.15);
      break;
    case 'grassland':
      // 亮绿色草原
      baseColor.lerp(new THREE.Color('#7cb342'), 0.3);
      baseColor.lerp(new THREE.Color('#8bc34a'), 0.1);
      break;
    case 'swamp':
      // 深绿/暗色沼泽
      baseColor.lerp(new THREE.Color('#3d5c3d'), 0.4);
      baseColor.lerp(new THREE.Color('#2a3a2a'), 0.15);
      break;
  }

  // Subtle height-based banding
  const band = Math.min(6, Math.floor(h)) % 3;
  const liftPerBand = [-0.04, 0.0, 0.04][band];

  // Minimal random jitter
  const jitter = (hash2(Math.floor(wx) + 5, Math.floor(wz) + 5) - 0.5) * 0.03;

  // Height-based lightness adjustment
  const heightLift = (h - 0.6) * 0.04;
  const lift = heightLift + liftPerBand + jitter;

  baseColor.offsetHSL(0, 0, lift);

  return baseColor;
}

/**
 * Gets combat modifiers for terrain at given position
 * 使用新的地形战斗系统
 */
export function getTerrainCombatModifiers(
  wx: number,
  wz: number,
  factionId?: FactionId,
  unitType?: UnitType
): { attack: number; defense: number } {
  const h = tileHeight(wx, wz);
  const terrainType = getTerrainType(wx, wz, h);

  // 使用新的地形战斗系统
  const attackMod = unitType
    ? getTerrainAttackModifier(terrainType, unitType, factionId)
    : 1.0;
  const defenseMod = unitType
    ? getTerrainDefenseModifier(terrainType, unitType, factionId)
    : 1.0;

  return { attack: attackMod, defense: defenseMod };
}

/**
 * 获取完整的地形修正（包含特殊效果）
 */
export function getFullTerrainModifier(
  wx: number,
  wz: number,
  unitType: UnitType,
  factionId?: FactionId
) {
  const h = tileHeight(wx, wz);
  const terrainType = getTerrainType(wx, wz, h);
  return getTerrainModifier(terrainType, unitType, factionId);
}
